import { useState, useCallback, useEffect } from 'react';
import { getErrorMessage } from '../helpers';

/**
 * Hook para gestión de selección y validación de archivos
 * @param {Object} config - Configuración del selector
 * @param {boolean} config.acumular - Si acumula archivos o solo permite uno
 * @param {number} config.maxSize - Tamaño máximo en bytes por archivo
 * @param {Function} config.onFilesChange - Callback ejecutado al cambiar archivos
 * @param {any} config.resetKey - Key para forzar reset del componente
 * @returns {{ files: Array, error: string, onDrop: Function, removeFile: Function, reset: Function }}
 */
export const useSelectorArchivos = ({ acumular, maxSize, onFilesChange, resetKey }) => {
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');

    // Limpiar archivos cuando cambia resetKey (modo acumular=false)
    useEffect(() => {
        if (resetKey !== undefined) {
            setFiles([]);
            setError('');
        }
    }, [resetKey]);

    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        setError('');

        // Validar archivos rechazados
        if (rejectedFiles.length > 0) {
            const rejection = rejectedFiles[0];
            const errorCode = rejection.errors[0]?.code;
            setError(getErrorMessage(errorCode, maxSize));
            return;
        }

        // Actualizar estado de archivos
        const nuevos = acumular ? [...files, ...acceptedFiles] : acceptedFiles;
        setFiles(nuevos);
        onFilesChange?.(nuevos);

        // En modo no-acumulativo, limpiar después de notificar al padre
        if (!acumular) {
            setTimeout(() => {
                setFiles([]);
            }, 100);
        }
    }, [files, acumular, maxSize, onFilesChange]);

    const removeFile = useCallback((fileToRemove) => {
        const nuevos = files.filter(file => file !== fileToRemove);
        setFiles(nuevos);
        onFilesChange?.(nuevos);
    }, [files, onFilesChange]);

    const reset = useCallback(() => {
        setFiles([]);
        setError('');
    }, []);

    return {
        files,
        error,
        onDrop,
        removeFile,
        reset
    };
};
